import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type Escalation } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat, StatusPill } from "@/components/dash/ui";

// Manual transitions (engine sets open->notified itself; users own the rest).
const TRANSITIONS: Record<string, string[]> = {
  open: ["acknowledged", "resolved", "dismissed"],
  notified: ["acknowledged", "resolved", "dismissed"],
  acknowledged: ["resolved", "dismissed", "open"],
  resolved: ["open"],
  dismissed: ["open"],
};
const FILTERS = ["active", "open", "notified", "acknowledged", "resolved", "dismissed", "all"];
const TYPE_LABEL: Record<string, string> = {
  unacked_priority: "Unacknowledged priority",
  management_action_overdue: "Management action overdue",
  trust_review_overdue: "Trust review overdue",
  delivery_failure: "Delivery failure",
};

// Where an escalation links back to — the source IRONDash surface, never a source system.
function sourceLink(e: Escalation): string | null {
  switch (e.source_kind) {
    case "alert":
    case "kpi":
      return "/decisions/priorities";
    case "management_action":
      return "/decisions/actions";
    case "trust_review":
      return "/trust/reviews";
    case "delivery":
      return "/notifications";
    default:
      return null;
  }
}

function Events({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["escalation-events", id], queryFn: () => dashApi.escalationEvents(id) });
  if (q.isLoading) return <p className="px-3 py-2 text-xs text-slate-500">Loading history…</p>;
  if (!q.data?.events.length) return <p className="px-3 py-2 text-xs text-slate-500">No history.</p>;
  return (
    <ul className="space-y-1.5 px-3 py-2">
      {q.data.events.map((e) => (
        <li key={e.id} className="flex items-start gap-2 text-[11px] text-slate-400">
          <span className="text-slate-600">{e.ts ? fmtDateTime(e.ts) : ""}</span>
          <span className="text-slate-300">{e.event_type}</span>
          {e.from_status && <span>{e.from_status} → {e.to_status}</span>}
          {e.note && <span className="text-slate-500">“{e.note}”</span>}
          {e.actor_email && <span className="ml-auto text-slate-600">{e.actor_email}</span>}
        </li>
      ))}
    </ul>
  );
}

function Row({ e, isAdmin }: { e: Escalation; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const mut = useMutation({
    mutationFn: (body: Parameters<typeof dashApi.updateEscalation>[1]) => dashApi.updateEscalation(e.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["escalations"] });
      qc.invalidateQueries({ queryKey: ["escalations-summary"] });
    },
  });
  const next = TRANSITIONS[e.status] ?? [];
  const link = sourceLink(e);
  return (
    <>
      <tr className="text-slate-300">
        <td className="py-2">
          <button type="button" className="text-left hover:text-brand-300" onClick={() => setOpen((o) => !o)}>{e.title}</button>
          <div className="text-[11px] text-slate-600">
            {TYPE_LABEL[e.escalation_type] ?? e.escalation_type}
            {e.escalation_count > 0 ? ` · notified ${e.escalation_count}×` : ""}
          </div>
        </td>
        <td className="py-2"><StatusPill status={e.severity} /></td>
        <td className="py-2"><StatusPill status={e.status} label={e.status} /></td>
        <td className="py-2 text-slate-400">{e.owner ?? "—"}</td>
        <td className="py-2 text-[11px] text-slate-500">{e.last_escalated_at ? fmtRelative(e.last_escalated_at) : "—"}</td>
        <td className="py-2 text-[11px] text-slate-500">{fmtRelative(e.first_seen_at)}</td>
        <td className="py-2 text-right">
          {isAdmin && next.length > 0 && (
            <select value="" onChange={(ev) => { if (ev.target.value) mut.mutate({ status: ev.target.value }); }}
              className="rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-[11px] text-slate-200 [color-scheme:dark]">
              <option value="">Move to…</option>
              {next.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="bg-ink-800/40">
          {e.reason && <p className="px-3 pt-2 text-xs text-slate-400">{e.reason}</p>}
          <p className="px-3 pt-1 text-[11px] text-slate-600">
            {e.source_kind ? `source: ${e.source_kind}${e.source_ref ? ` (${e.source_ref})` : ""}` : ""}
            {link && <Link to={link} className="ml-2 text-brand-300 hover:underline">View source →</Link>}
          </p>
          <Events id={e.id} />
        </td></tr>
      )}
    </>
  );
}

export default function EscalationsPage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [filter, setFilter] = useState("active");
  const q = useQuery({ queryKey: ["escalations", filter], queryFn: () => dashApi.escalations(filter === "all" ? undefined : filter) });
  const sum = useQuery({ queryKey: ["escalations-summary"], queryFn: dashApi.escalationsSummary });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Escalations"
        description="IRONDash raises an escalation when a critical issue stays unacknowledged, an action or review goes overdue, or deliveries keep failing. Deduplicated and notified sparingly (re-notify cooldown + quiet hours). Acknowledging or resolving only changes IRONDash metadata — no source system is ever written."
      />
      {sum.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Active" value={sum.data.active} />
          <Stat label="Unacknowledged" value={sum.data.unacknowledged} />
          <Stat label="Critical" value={sum.data.by_severity?.critical ?? 0} />
          <Stat label="Warning" value={sum.data.by_severity?.warning ?? 0} />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`rounded-md px-2.5 py-1 text-xs ${filter === f ? "bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40" : "bg-ink-800 text-slate-400 hover:bg-ink-700"}`}>
            {f}
          </button>
        ))}
      </div>
      <Panel title={`Escalations (${q.data?.escalations.length ?? 0})`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.escalations.length ? (
          <EmptyState>No escalations in this view. This is the healthy state — nothing is being escalated.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="pb-2 font-medium">Escalation</th><th className="pb-2 font-medium">Severity</th>
              <th className="pb-2 font-medium">Status</th><th className="pb-2 font-medium">Owner</th>
              <th className="pb-2 font-medium">Last escalated</th><th className="pb-2 font-medium">First seen</th><th className="pb-2" />
            </tr></thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data.escalations.map((e) => <Row key={e.id} e={e} isAdmin={isAdmin} />)}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
